import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectProposalOutlineSections } from "slices/proposalOutline/selectors";

import { resolveDocumentTheme } from "./themeTokens";

// Ignores collapsed rows' hidden children, so groups continuing into them render as unclosed; used until table ref is ready, or as fallback.
const getRawNeighborGrouping = (row, nextgenDocumentId, sections) => {
  const { index } = row;
  const isCollapsed = row.getCanExpand() && !row.getIsExpanded();
  const prevSection = sections[index - 1];
  const nextSection = sections[index + 1];

  return {
    isFirstInGroup:
      index === 0 || prevSection?.nextgenDocumentId !== nextgenDocumentId,
    isLastInGroup:
      isCollapsed ||
      index === sections.length - 1 ||
      nextSection?.nextgenDocumentId !== nextgenDocumentId,
    isLinked: Boolean(nextgenDocumentId),
  };
};

// Table's row model already excludes collapsed rows' descendants, so neighbors read correctly here without special-casing collapse state.
const getVisibleNeighborGrouping = (row, nextgenDocumentId, visibleRows) => {
  const position = visibleRows.findIndex(
    (visibleRow) => visibleRow.id === row.id
  );
  const prevRow = visibleRows[position - 1];
  const nextRow = visibleRows[position + 1];

  return {
    isFirstInGroup:
      position <= 0 ||
      prevRow?.original?.nextgenDocumentId !== nextgenDocumentId,
    isLastInGroup:
      position === visibleRows.length - 1 ||
      nextRow?.original?.nextgenDocumentId !== nextgenDocumentId,
    isLinked: Boolean(nextgenDocumentId),
  };
};

export const useLinkGrouping = (row, nextgenDocumentId, getTable) => {
  const sections = useSelector(selectProposalOutlineSections);
  // Undefined only on the very first render, before the table ref commits.
  const visibleRows = getTable?.()?.getRowModel()?.rows;

  return useMemo(() => {
    const grouping = visibleRows
      ? getVisibleNeighborGrouping(row, nextgenDocumentId, visibleRows)
      : getRawNeighborGrouping(row, nextgenDocumentId, sections);

    return {
      ...grouping,
      documentTheme: resolveDocumentTheme(row.original?.documentTheme),
    };
  }, [row, nextgenDocumentId, sections, visibleRows]);
};

export default useLinkGrouping;
